/**
 * City slugs ↔ city names.
 *
 * WHY THIS IS NOT A QUERY. `/:locale/:city/:category` carries a slug, but
 * `business_locations.city` stores the real Norwegian name — `Bærum`, `Tromsø` —
 * and `searchBusinesses` matches it with `ilike`, which is case-insensitive but
 * not diacritic-insensitive. So `baerum` has to become `Bærum` before the query
 * is built, and nothing in the applied schema can do that: there is no `cities`
 * table, and no `search_businesses()` RPC to normalise inside (see the header of
 * `packages/api/src/businesses.ts`).
 *
 * The list below is therefore a LOOKUP, not content: the twenty largest
 * Norwegian municipalities, which is what the SEO city links point at. Any other
 * slug still works — `cityNameFromSlug` title-cases it — it simply cannot
 * recover a letter the slug dropped. When the `cities` table from Docs/04 lands,
 * or the Kartverket lookup in Docs/07 §1, this file becomes a query.
 *
 * City names are proper nouns and are identical in both locales, so nothing here
 * goes through i18n.
 */

export type City = {
  slug: string
  name: string
}

export const CITIES: readonly City[] = [
  { slug: 'oslo', name: 'Oslo' },
  { slug: 'bergen', name: 'Bergen' },
  { slug: 'trondheim', name: 'Trondheim' },
  { slug: 'stavanger', name: 'Stavanger' },
  { slug: 'baerum', name: 'Bærum' },
  { slug: 'kristiansand', name: 'Kristiansand' },
  { slug: 'drammen', name: 'Drammen' },
  { slug: 'asker', name: 'Asker' },
  { slug: 'lillestrom', name: 'Lillestrøm' },
  { slug: 'fredrikstad', name: 'Fredrikstad' },
  { slug: 'sandnes', name: 'Sandnes' },
  { slug: 'tromso', name: 'Tromsø' },
  { slug: 'sarpsborg', name: 'Sarpsborg' },
  { slug: 'bodo', name: 'Bodø' },
  { slug: 'alesund', name: 'Ålesund' },
  { slug: 'sandefjord', name: 'Sandefjord' },
  { slug: 'haugesund', name: 'Haugesund' },
  { slug: 'tonsberg', name: 'Tønsberg' },
  { slug: 'moss', name: 'Moss' },
  { slug: 'skien', name: 'Skien' },
] as const

/** The city the site leads with when a link needs one and the visitor has not chosen. */
export const DEFAULT_CITY = CITIES[0] as City

const BY_SLUG = new Map(CITIES.map((city) => [city.slug, city]))

/**
 * `baerum` → `Bærum`. An unknown slug is title-cased word by word, so
 * `/no/lorenskog/elektriker` renders "Elektriker i Lorenskog" rather than a
 * broken heading — the page is still honest, it has simply lost the `ø`.
 */
export const cityNameFromSlug = (slug: string): string =>
  BY_SLUG.get(slug.toLowerCase())?.name ??
  slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toLocaleUpperCase('nb-NO') + word.slice(1))
    .join(' ')

/** True when the slug is one of the cities the site links to itself. */
export const isKnownCity = (slug: string): boolean => BY_SLUG.has(slug.toLowerCase())

/** Cities other than the one being viewed — the "nearby cities" SEO block. */
export const otherCities = (slug: string, limit: number): City[] =>
  CITIES.filter((city) => city.slug !== slug.toLowerCase()).slice(0, limit)
